import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Link,
  InputAdornment,
  IconButton,
  useTheme,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import agent from "../../api/agent";
import image from "../../../../src/assets/images/users/image.jpg";
import loginImage2 from "../../../../src/assets/images/users/UH_logo.svg";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useStore } from "../../stores/store";
import { observer } from "mobx-react-lite";

const ForgotPasswordPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const { userStore } = useStore();
  const [step, setStep] = useState<"email" | "verify">("email");
  const [email, setEmail] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isResendingOtp, setIsResendingOtp] = useState(false);

  const handleTogglePassword = () => {
    setShowPassword((prev) => !prev);
  };

  const handleOtpChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
  ) => {
    const value = e.target.value.replace(/[^0-9]/g, "").slice(0, 6);
    e.target.value = value;
    handleChange(e);
  };

  useEffect(() => {
    if (!userStore.loading && userStore.isLoggedIn) {
      navigate("/dashboard");
    }
  }, [userStore.loading, userStore.isLoggedIn, navigate]);

  const emailValidationSchema = Yup.object().shape({
    email: Yup.string()
      .email("Invalid email address")
      .required("Email is required"),
  });

  const resetValidationSchema = Yup.object().shape({
    otp: Yup.string()
      .required("OTP is required")
      .length(6, "OTP must be 6 characters"),
    newPassword: Yup.string()
      .min(6, "Password must be at least 6 characters")
      .required("Password is required"),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("newPassword")], "Passwords must match")
      .required("Confirm password is required"),
  });

  const handleEmailSubmit = async (values: any, { setSubmitting }: any) => {
    try {
      const response = await agent.User.forgotPassword(values.email);
      if (response.status === "success" || response.data) {
        setEmail(values.email);
        setStep("verify");
        toast.success("OTP sent to your email");
      } else {
        throw new Error(response.message || "Failed to send OTP");
      }
    } catch (err: any) {
      toast.error(
        err?.message || err?.data?.message || "Failed to send OTP"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleResetPassword = async (
    values: any,
    { setSubmitting }: any
  ) => {
    try {
      const response = await agent.User.resetPassword({
        email,
        otp: values.otp,
        newPassword: values.newPassword,
      });
      if (response.status === "success" || response.data) {
        toast.success("Password reset successfully");
        navigate("/login");
      } else {
        throw new Error(response.message || "Failed to reset password");
      }
    } catch (err: any) {
      toast.error(
        err?.message || err?.data?.message || "Failed to reset password"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendOtp = async () => {
    try {
      setIsResendingOtp(true);
      const response = await agent.User.forgotPassword(email);
      if (response.status === "success" || response.data) {
        toast.success("OTP resent to your email");
      } else {
        throw new Error(response.message || "Failed to resend OTP");
      }
    } catch (err: any) {
      toast.error(
        err?.message || err?.data?.message || "Failed to resend OTP"
      );
    } finally {
      setIsResendingOtp(false);
    }
  };

  if (userStore.loading) return <CircularProgress />;

  return (
    <Box
      display="flex"
      flexDirection={{ xs: "column", md: "row" }}
      height="100vh"
      sx={{ overflow: "hidden" }}
    >
      {!isSmallScreen && (
        <Box
          sx={{
            width: "416.23px",
            height: "100vh",
            overflow: "hidden",
            flexShrink: 0,
          }}
        >
          <img
            src={image}
            alt="Login visual"
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        </Box>
      )}

      <Box
        flex={1}
        display="flex"
        alignItems="center"
        justifyContent="center"
        flexDirection="column"
        bgcolor="#ffffff"
        p={2}
        sx={{
          height: "100vh",
          overflow: "hidden",
        }}
      >
        <Box display="flex" justifyContent="center" mb={2}>
          <img
            src={loginImage2}
            alt="Logo"
            style={{ width: "139px", height: "120px" }}
          />
        </Box>

        <Typography
          variant="h5"
          component="h1"
          gutterBottom
          sx={{
            fontFamily: "Source Sans 3, sans-serif",
            fontWeight: 600,
            fontSize: "24px",
            lineHeight: 1.3,
            letterSpacing: "0.15px",
            color: "#016931",
            textAlign: "center",
            mb: 1,
          }}
        >
          Reset Password
        </Typography>

        <Paper
          elevation={0}
          sx={{
            width: "100%",
            color: "#050606",
            maxWidth: "400px",
            padding: { xs: 0, sm: 0 },
            borderRadius: "8px",
          }}
        >
          {step === "email" ? (
            <Formik
              initialValues={{ email: "" }}
              validationSchema={emailValidationSchema}
              onSubmit={handleEmailSubmit}
            >
              {({
                values,
                handleChange,
                handleBlur,
                touched,
                errors,
                isSubmitting,
              }) => (
                <Form
                  style={{ display: "flex", flexDirection: "column", gap: 3 }}
                >
                  <Box>
                    <Typography
                      sx={{
                        color: "#016931",
                        fontSize: "14px",
                        mb: "3px",
                      }}
                    >
                      Email ID:{" "}
                      <span style={{ color: "#333333", fontWeight: "500" }}>
                        *
                      </span>
                    </Typography>
                    <TextField
                      name="email"
                      placeholder="Enter email address"
                      fullWidth
                      size="small"
                      variant="outlined"
                      value={values.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.email && Boolean(errors.email)}
                      helperText={touched.email && errors.email}
                    />
                  </Box>

                  <Button
                    variant="contained"
                    type="submit"
                    disabled={isSubmitting}
                    sx={{
                      width: "100%",
                      height: "40px",
                      padding: "10px 40px",
                      borderRadius: "4px",
                      backgroundColor: "#016931",
                      color: "#ffffff",
                      textTransform: "none",
                      "&:hover": {
                        backgroundColor: "#015627",
                      },
                      "&:disabled": {
                        backgroundColor: "#ccc",
                      },
                    }}
                  >
                    {isSubmitting ? "Sending OTP..." : "Send OTP"}
                  </Button>

                  <Box textAlign="center">
                    <Typography sx={{ fontSize: "14px", color: "#666" }}>
                      Remember password?{" "}
                      <Link
                        href="/login"
                        underline="none"
                        sx={{
                          color: "#016931",
                          cursor: "pointer",
                          "&:hover": {
                            textDecoration: "underline",
                          },
                        }}
                      >
                        Back to login
                      </Link>
                    </Typography>
                  </Box>
                </Form>
              )}
            </Formik>
          ) : (
            <Formik
              initialValues={{
                otp: "",
                newPassword: "",
                confirmPassword: "",
              }}
              validationSchema={resetValidationSchema}
              onSubmit={handleResetPassword}
            >
              {({
                values,
                handleChange,
                handleBlur,
                touched,
                errors,
                isSubmitting,
              }) => (
                <Form
                  style={{ display: "flex", flexDirection: "column", gap: 3 }}
                >
                  <Box>
                    <Typography
                      sx={{
                        color: "#016931",
                        fontSize: "14px",
                        mb: "3px",
                      }}
                    >
                      OTP:{" "}
                      <span style={{ color: "#333333", fontWeight: "500" }}>
                        *
                      </span>
                    </Typography>
                    <TextField
                      name="otp"
                      placeholder="Enter 6-digit OTP"
                      fullWidth
                      size="small"
                      variant="outlined"
                      inputProps={{ maxLength: 6, inputMode: "numeric" }}
                      value={values.otp}
                      onChange={(e) => handleOtpChange(e, handleChange)}
                      onBlur={handleBlur}
                      error={touched.otp && Boolean(errors.otp)}
                      helperText={touched.otp && errors.otp}
                    />
                    <Box textAlign="right" mt={1}>
                      <Button
                        onClick={handleResendOtp}
                        disabled={isResendingOtp}
                        sx={{
                          color: "#016931",
                          fontSize: "14px",
                          textTransform: "none",
                          padding: 0,
                          minWidth: "auto",
                          "&:hover": {
                            backgroundColor: "transparent",
                            textDecoration: "underline",
                          },
                        }}
                      >
                        {isResendingOtp ? "Resending..." : "Resend OTP"}
                      </Button>
                    </Box>
                  </Box>

                  <Box>
                    <Typography
                      sx={{
                        color: "#016931",
                        fontSize: "14px",
                        mb: "3px",
                        mt: "4px",
                      }}
                    >
                      New Password:{" "}
                      <span style={{ color: "#333333", fontWeight: "500" }}>
                        *
                      </span>
                    </Typography>
                    <TextField
                      name="newPassword"
                      placeholder="Enter new password"
                      fullWidth
                      size="small"
                      variant="outlined"
                      type={showPassword ? "text" : "password"}
                      value={values.newPassword}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.newPassword && Boolean(errors.newPassword)}
                      helperText={touched.newPassword && errors.newPassword}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={handleTogglePassword}
                              edge="end"
                            >
                              {showPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>

                  <Box>
                    <Typography
                      sx={{
                        color: "#016931",
                        fontSize: "14px",
                        mb: "3px",
                        mt: "4px",
                      }}
                    >
                      Confirm Password:{" "}
                      <span style={{ color: "#333333", fontWeight: "500" }}>
                        *
                      </span>
                    </Typography>
                    <TextField
                      name="confirmPassword"
                      placeholder="Confirm password"
                      fullWidth
                      size="small"
                      variant="outlined"
                      type={showPassword ? "text" : "password"}
                      value={values.confirmPassword}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={
                        touched.confirmPassword &&
                        Boolean(errors.confirmPassword)
                      }
                      helperText={
                        touched.confirmPassword && errors.confirmPassword
                      }
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={handleTogglePassword}
                              edge="end"
                            >
                              {showPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>

                  <Button
                    variant="contained"
                    type="submit"
                    disabled={isSubmitting}
                    sx={{
                      width: "100%",
                      height: "40px",
                      padding: "10px 40px",
                      borderRadius: "4px",
                      backgroundColor: "#016931",
                      color: "#ffffff",
                      textTransform: "none",
                      "&:hover": {
                        backgroundColor: "#015627",
                      },
                      "&:disabled": {
                        backgroundColor: "#ccc",
                      },
                    }}
                  >
                    {isSubmitting ? "Resetting..." : "Reset Password"}
                  </Button>

                  <Box textAlign="center">
                    <Button
                      type="button"
                      sx={{
                        color: "#016931",
                        textTransform: "none",
                        fontSize: "14px",
                        "&:hover": {
                          backgroundColor: "transparent",
                          textDecoration: "underline",
                        },
                      }}
                      onClick={() => setStep("email")}
                    >
                      Back to email
                    </Button>
                  </Box>
                </Form>
              )}
            </Formik>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default observer(ForgotPasswordPage);
